import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useFavourites } from '../context/FavouritesContext';
import { useAuth } from '../context/AuthContext';
import { CreatorBadges } from './VerifiedBadge';
import { toast } from 'sonner';

const categoryTint = {
  crochet: 'bg-chilli/10',
  stickers: 'bg-mustard-tint',
  art: 'bg-bluebell-tint',
  keyrings: 'bg-fern-tint',
};

const categoryPill = {
  crochet: 'bg-chilli/10 text-chilli',
  stickers: 'bg-mustard-tint text-mustard',
  art: 'bg-bluebell-tint text-bluebell',
  keyrings: 'bg-fern-tint text-fern',
};

export const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { user } = useAuth();
  const favourite = isFavourite(product.id);
  const tint = categoryTint[product.category] || 'bg-butter-mid';
  const pill = categoryPill[product.category] || 'bg-butter-mid text-text-secondary';

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }
    try {
      await addToCart(product.id);
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to save favourites');
      return;
    }
    toggleFavourite(product.id);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group flex flex-col bg-butter rounded-[1.75rem] overflow-hidden border-2 border-butter-mid hover:border-butter-deep hover:-translate-y-1.5 hover:shadow-md transition-all duration-300 ease-out animate-fade-in"
      data-testid={`product-card-${product.id}`}
    >
      {/* Tinted image panel */}
      <div className={`relative h-52 overflow-hidden ${tint}`}>
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-text-tertiary font-display font-bold lowercase">
            no image
          </div>
        )}

        {/* Favourite Button */}
        <Button
          variant="ghost"
          size="icon"
          className={`absolute top-3 right-3 rounded-full bg-butter/85 backdrop-blur-sm hover:bg-butter ${favourite ? 'text-chilli' : 'text-text-secondary'}`}
          onClick={handleFavorite}
          data-testid={`favourite-btn-${product.id}`}
        >
          <Heart className={`w-4 h-4 ${favourite ? 'fill-current' : ''}`} />
        </Button>

        {/* Quick Add */}
        <div className="absolute bottom-3 left-3 right-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <Button
            className="w-full lowercase font-display font-bold bg-chilli text-butter hover:bg-chilli-deep text-sm rounded-full"
            onClick={handleAddToCart}
            data-testid={`add-cart-btn-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            add to cart
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-grow p-5">
        <h3 className="font-display font-bold text-lg lowercase leading-tight text-text-primary group-hover:text-chilli transition-colors line-clamp-1">
          {product.name}
        </h3>

        {product.store_name && (
          <p className="mt-1 text-sm text-text-secondary flex items-center gap-1">
            by {product.creator_name || product.store_name}
            <CreatorBadges
              isVerified={product.is_verified}
              isFoundingCreator={product.is_founding_creator}
              tier={product.tier}
              endorsements={product.endorsement_count}
              size="sm"
            />
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 mt-auto pt-4 border-t-2 border-dashed border-butter-deep/50">
          <span className="font-display font-extrabold text-base text-fern bg-fern-tint/60 px-3 py-1 rounded-lg">
            ${product.price?.toFixed(2)}
          </span>
          {product.category && (
            <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] ${pill}`}>
              {product.category}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
